import { useState, useMemo, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Swal from 'sweetalert2';
import { adminVideoData } from '@/mocks/adminVideos';
import type { AdminVideo, VideoCategory, VideoSourceType } from '@/mocks/adminVideos';
import { usePlanPermissions } from '@/hooks/usePlanPermissions';
import { useUpgradeModal } from '@/context/UpgradeModalContext';
import StorageBar from './components/StorageBar';
import VideoGrid from './components/VideoGrid';
import VideoFormModal from './components/VideoFormModal';
import VideoHistoryModal from './components/VideoHistoryModal';

const allCategories: VideoCategory[] = ['programas', 'entrevistas', 'eventos', 'shorts', 'promociones'];

const sharedSwal = Swal.mixin({
  customClass: {
    popup: 'rounded-2xl',
    title: 'font-bold',
    confirmButton: 'rounded-lg px-5 py-2 text-sm font-bold whitespace-nowrap',
    cancelButton: 'rounded-lg px-5 py-2 text-sm font-bold whitespace-nowrap',
    htmlContainer: 'text-sm',
  },
  buttonsStyling: false,
});

function showPlanBlockAlert(onConfirm: () => void) {
  return sharedSwal.fire({
    title: 'Función Premium',
    html: '<p class="text-sm text-foreground-500 leading-relaxed">Los videos son exclusivos del plan Premium. Actualizá tu plan para subir y gestionar videos.</p>',
    icon: 'warning',
    confirmButtonText: 'Ver planes',
    confirmButtonColor: '#f59e0b',
    showCancelButton: true,
    cancelButtonText: 'Cerrar',
    cancelButtonColor: '#9ca3af',
  }).then((result) => {
    if (result.isConfirmed) onConfirm();
  });
}

function showSuccessToast(text: string) {
  Swal.fire({
    toast: true,
    position: 'top-end',
    title: text,
    icon: 'success',
    showConfirmButton: false,
    timer: 2200,
    timerProgressBar: true,
    customClass: { popup: 'rounded-xl' },
  });
}

export default function AdminVideos() {
  const { isPremium, isFree, planId, storageUsedGB, storageTotalGB, storagePercentage } = usePlanPermissions();
  const { openUpgradeModal } = useUpgradeModal();

  const [videos, setVideos] = useState<AdminVideo[]>([]);

  const [filterCategory, setFilterCategory] = useState<VideoCategory | 'todas'>('todas');
  const [filterStatus, setFilterStatus] = useState<AdminVideo['status'] | 'todos'>('todos');
  const [sourceFilter, setSourceFilter] = useState<VideoSourceType | 'todos'>('todos');
  const [searchQuery, setSearchQuery] = useState('');

  const [formModalOpen, setFormModalOpen] = useState(false);
  const [editingVideo, setEditingVideo] = useState<AdminVideo | null>(null);

  const [historyVideo, setHistoryVideo] = useState<AdminVideo | null>(null);
  const [historyModalOpen, setHistoryModalOpen] = useState(false);

  const [pageState, setPageState] = useState<'loading' | 'error' | 'ready'>('loading');

  // Simular carga
  useEffect(() => {
    const timer = setTimeout(() => {
      const shouldFail = Math.random() < 0.06;
      if (shouldFail) {
        setPageState('error');
      } else {
        setVideos(adminVideoData.videos);
        setPageState('ready');
      }
    }, 800 + Math.random() * 800);
    return () => clearTimeout(timer);
  }, []);

  const handleRetry = useCallback(() => {
    setPageState('loading');
    const timer = setTimeout(() => {
      const shouldFail = Math.random() < 0.04;
      if (shouldFail) {
        setPageState('error');
      } else {
        setVideos(adminVideoData.videos);
        setPageState('ready');
      }
    }, 500 + Math.random() * 600);
    return () => clearTimeout(timer);
  }, []);

  /* ─── Filtrado ─── */
  const filteredVideos = useMemo(() => {
    return videos.filter((v) => {
      const matchCat = filterCategory === 'todas' || v.category === filterCategory;
      const matchStatus = filterStatus === 'todos' || v.status === filterStatus;
      const matchSource = sourceFilter === 'todos' || v.sourceType === sourceFilter;
      const matchSearch =
        !searchQuery ||
        v.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        v.description.toLowerCase().includes(searchQuery.toLowerCase());
      return matchCat && matchStatus && matchSource && matchSearch;
    });
  }, [videos, filterCategory, filterStatus, sourceFilter, searchQuery]);

  /* ─── Stats ─── */
  const stats = useMemo(() => {
    const featured = videos.filter((v) => v.isFeatured).length;
    const thisMonth = videos.filter((v) => {
      const now = new Date();
      const d = new Date(v.uploadedAt);
      return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
    }).length;
    const published = videos.filter((v) => v.status === 'publicado').length;
    const drafts = videos.filter((v) => v.status === 'borrador').length;
    const scheduled = videos.filter((v) => v.status === 'programado').length;
    return { featured, thisMonth, total: videos.length, published, drafts, scheduled };
  }, [videos]);

  /* ─── Storage display ─── */
  const storageDisplay = useMemo(() => {
    const used = `${storageUsedGB.toFixed(1)} GB`;
    const total = `${storageTotalGB} GB`;
    return {
      used,
      total,
      percentage: storagePercentage,
      usedGB: storageUsedGB,
      totalGB: storageTotalGB,
      videoCount: videos.length,
    };
  }, [storageUsedGB, storageTotalGB, storagePercentage, videos.length]);

  /* ─── Handlers ─── */
  const handleCreate = () => {
    if (!isPremium) { showPlanBlockAlert(openUpgradeModal); return; }
    if (storagePercentage >= 95) {
      sharedSwal.fire({
        title: 'Almacenamiento lleno',
        html: `<p class="text-sm text-foreground-500 leading-relaxed">Tu almacenamiento est\u00e1 al <strong>${storagePercentage}%</strong> (${storageUsedGB.toFixed(1)} GB de ${storageTotalGB} GB). Elimin\u00e1 videos viejos o mejor\u00e1 tu plan para seguir subiendo.</p>`,
        icon: 'error',
        confirmButtonText: 'Administrar almacenamiento',
        confirmButtonColor: '#ef4444',
        showCancelButton: true,
        cancelButtonText: 'Cerrar',
        cancelButtonColor: '#9ca3af',
      }).then((result) => {
        if (result.isConfirmed) {
          Swal.fire({
            toast: true,
            position: 'top-end',
            title: 'Revis\u00e1 tus videos y elimin\u00e1 los que no necesites.',
            icon: 'info',
            showConfirmButton: false,
            timer: 3000,
            timerProgressBar: true,
            customClass: { popup: 'rounded-xl' },
          });
        }
      });
      return;
    }
    setEditingVideo(null);
    setFormModalOpen(true);
  };

  const handleEdit = (video: AdminVideo) => {
    if (!isPremium) { showPlanBlockAlert(openUpgradeModal); return; }
    setEditingVideo(video);
    setFormModalOpen(true);
  };

  const handleSave = (data: {
    title: string;
    description: string;
    category: VideoCategory;
    sourceType: VideoSourceType;
    sourceUrl: string;
    duration: string;
    status: AdminVideo['status'];
    isFeatured: boolean;
    scheduledAt?: string;
  }) => {
    if (editingVideo) {
      setVideos((prev) =>
        prev.map((v) =>
          v.id === editingVideo.id
            ? {
                ...v,
                title: data.title,
                description: data.description,
                category: data.category,
                sourceType: data.sourceType,
                sourceUrl: data.sourceUrl,
                duration: data.duration,
                status: data.status,
                isFeatured: data.isFeatured,
                scheduledAt: data.scheduledAt,
              }
            : v
        )
      );
    } else {
      const newVideo: AdminVideo = {
        id: Math.max(...videos.map((v) => v.id), 0) + 1,
        title: data.title,
        description: data.description,
        category: data.category,
        thumbnailUrl:
          'https://readdy.ai/api/search-image?query=New%20video%20content%20placeholder%20thumbnail%2C%20modern%20media%20player%20interface%20design%2C%20clean%20dark%20background%20with%20subtle%20gradient%2C%20professional%20video%20production%20aesthetic%2C%20generic%20entertainment%20placeholder&width=1280&height=720&seq=video-placeholder-new&orientation=landscape',
        sourceType: data.sourceType,
        sourceUrl: data.sourceUrl,
        duration: data.duration,
        status: data.status,
        isFeatured: data.isFeatured,
        fileSize: '0 MB',
        uploadedAt: new Date().toISOString(),
        scheduledAt: data.scheduledAt,
      };
      setVideos((prev) => [newVideo, ...prev]);
    }
    setFormModalOpen(false);
    setEditingVideo(null);
    showSuccessToast(editingVideo ? 'Video actualizado.' : 'Video agregado exitosamente.');
  };

  const handleDelete = (video: AdminVideo) => {
    sharedSwal
      .fire({
        title: '¿Eliminar video?',
        html: `<p class="text-sm text-foreground-500">Se eliminará permanentemente <strong>&ldquo;${escapeHtml(video.title)}&rdquo;</strong>. Esta acción no se puede deshacer.</p>`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Eliminar',
        cancelButtonText: 'Cancelar',
        confirmButtonColor: '#ef4444',
        cancelButtonColor: '#9ca3af',
      })
      .then((result) => {
        if (result.isConfirmed) {
          setVideos((prev) => prev.filter((v) => v.id !== video.id));
          showSuccessToast('Video eliminado.');
        }
      });
  };

  const handleToggleFeatured = (video: AdminVideo) => {
    if (!isPremium) { showPlanBlockAlert(openUpgradeModal); return; }
    const newFeatured = !video.isFeatured;
    setVideos((prev) =>
      prev.map((v) => (v.id === video.id ? { ...v, isFeatured: newFeatured } : v))
    );
    showSuccessToast(newFeatured ? 'Marcado como destacado.' : 'Quitado de destacados.');
  };

  /* ─── Share video ─── */
  const handleShareVideo = (video: AdminVideo) => {
    const shareUrl = `${window.location.origin}/video/${video.id}`;

    sharedSwal.fire({
      title: 'Compartir video',
      html: `
        <div class="space-y-3 text-left">
          <div class="bg-background-100 rounded-xl p-3 flex items-center gap-3">
            <div class="w-20 h-12 rounded-lg overflow-hidden bg-background-200 shrink-0">
              <img src="${video.thumbnailUrl}" alt="${escapeHtml(video.title)}" class="w-full h-full object-cover" />
            </div>
            <div class="min-w-0">
              <p class="text-sm font-semibold text-foreground-900 line-clamp-1">${escapeHtml(video.title)}</p>
              <p class="text-[10px] text-foreground-400 mt-0.5">${video.duration} &bull; ${video.fileSize}</p>
            </div>
          </div>
          <div class="flex items-center gap-2">
            <input
              type="text"
              value="${shareUrl}"
              readonly
              class="flex-1 px-3 py-2 rounded-xl bg-background-100 border border-background-200 text-foreground-700 text-xs focus:outline-none"
              id="swal-share-video-url"
            />
            <button
              id="swal-copy-video-btn"
              class="px-3 py-2 rounded-xl bg-accent-500 text-background-50 text-xs font-semibold hover:bg-accent-600 cursor-pointer transition-colors whitespace-nowrap"
            >
              <i class="ri-file-copy-line mr-1"></i> Copiar
            </button>
          </div>
          <div class="flex items-center gap-2">
            <button id="swal-share-video-fb" class="flex-1 px-3 py-2 rounded-xl bg-[#1877F2] text-white text-xs font-semibold hover:opacity-90 cursor-pointer transition-opacity whitespace-nowrap text-center">
              <i class="ri-facebook-fill mr-1"></i> Facebook
            </button>
            <button id="swal-share-video-tw" class="flex-1 px-3 py-2 rounded-xl bg-black text-white text-xs font-semibold hover:opacity-80 cursor-pointer transition-opacity whitespace-nowrap text-center">
              <i class="ri-twitter-x-fill mr-1"></i> X
            </button>
            <button id="swal-share-video-wa" class="flex-1 px-3 py-2 rounded-xl bg-[#25D366] text-white text-xs font-semibold hover:opacity-90 cursor-pointer transition-opacity whitespace-nowrap text-center">
              <i class="ri-whatsapp-fill mr-1"></i> WhatsApp
            </button>
          </div>
        </div>
      `,
      showConfirmButton: false,
      showCloseButton: true,
      customClass: { popup: 'rounded-2xl max-w-md' },
      didOpen: () => {
        const copyBtn = document.getElementById('swal-copy-video-btn');
        const urlInput = document.getElementById('swal-share-video-url') as HTMLInputElement;
        if (copyBtn && urlInput) {
          copyBtn.addEventListener('click', () => {
            navigator.clipboard.writeText(shareUrl).then(() => {
              copyBtn.innerHTML = '<i class="ri-check-line mr-1"></i> Copiado';
              copyBtn.classList.add('bg-emerald-500');
              copyBtn.classList.remove('bg-accent-500');
              setTimeout(() => {
                copyBtn.innerHTML = '<i class="ri-file-copy-line mr-1"></i> Copiar';
                copyBtn.classList.remove('bg-emerald-500');
                copyBtn.classList.add('bg-accent-500');
              }, 2000);
            });
          });
        }
        const fbBtn = document.getElementById('swal-share-video-fb');
        const twBtn = document.getElementById('swal-share-video-tw');
        const waBtn = document.getElementById('swal-share-video-wa');
        const encodedUrl = encodeURIComponent(shareUrl);
        const encodedTitle = encodeURIComponent(video.title);
        if (fbBtn) fbBtn.addEventListener('click', () => window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`, '_blank'));
        if (twBtn) twBtn.addEventListener('click', () => window.open(`https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}`, '_blank'));
        if (waBtn) waBtn.addEventListener('click', () => window.open(`https://wa.me/?text=${encodedTitle}%20${encodedUrl}`, '_blank'));
      },
    });
  };

  /* ─── History ─── */
  const handleHistory = (video: AdminVideo) => {
    setHistoryVideo(video);
    setHistoryModalOpen(true);
  };

  /* ─── Loading Skeleton ─── */
  if (pageState === 'loading') {
    return (
      <div className="min-h-screen bg-background-100">
        <header className="lg:hidden sticky top-0 z-30 bg-background-50/98 backdrop-blur-2xl border-b border-background-200 safe-top">
          <div className="flex items-center px-4 h-14">
            <div className="w-7 h-7 rounded-lg bg-background-200 animate-pulse"></div>
          </div>
        </header>
        <div className="flex">
  
          <main className="flex-1 min-w-0">
            <div className="hidden lg:flex items-center h-16 px-8 border-b border-background-200 bg-background-50 sticky top-0 z-20">
              <div className="w-40 h-4 rounded-md bg-background-200 animate-pulse"></div>
            </div>
            <div className="p-4 md:p-6 lg:p-8 space-y-5">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {[...Array(4)].map((_, i) => (
                  <div key={i} className="bg-background-50 rounded-2xl border border-background-200 p-4">
                    <div className="w-16 h-3 rounded bg-background-200 animate-pulse mb-2"></div>
                    <div className="w-10 h-7 rounded bg-background-200 animate-pulse mb-1"></div>
                    <div className="w-20 h-2 rounded bg-background-200 animate-pulse"></div>
                  </div>
                ))}
              </div>
              <div className="bg-background-50 rounded-2xl border border-background-200 p-5">
                <div className="w-40 h-4 rounded bg-background-200 animate-pulse mx-auto mb-3"></div>
                <div className="w-60 h-3 rounded bg-background-200 animate-pulse mx-auto"></div>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="bg-background-50 rounded-2xl border border-background-200 p-4">
                    <div className="aspect-video w-full rounded-lg bg-background-200 animate-pulse mb-3"></div>
                    <div className="w-3/4 h-4 rounded bg-background-200 animate-pulse mb-2"></div>
                    <div className="w-full h-3 rounded bg-background-200 animate-pulse mb-1"></div>
                    <div className="w-1/2 h-3 rounded bg-background-200 animate-pulse"></div>
                  </div>
                ))}
              </div>
            </div>
          </main>
        </div>
      </div>
    );
  }

  /* ─── Error State ─── */
  if (pageState === 'error') {
    return (
      <div className="min-h-screen bg-background-100">
        <header className="lg:hidden sticky top-0 z-30 bg-background-50/98 backdrop-blur-2xl border-b border-background-200 safe-top">
          <div className="flex items-center px-4 h-14">
            <div className="w-7 h-7 rounded-lg bg-accent-500 flex items-center justify-center">
              <i className="ri-video-line text-background-50 text-xs"></i>
            </div>
          </div>
        </header>
        <div className="flex">
  
          <main className="flex-1 min-w-0 flex items-center justify-center p-8">
            <div className="text-center max-w-sm">
              <div className="w-16 h-16 mx-auto rounded-2xl bg-red-100 flex items-center justify-center mb-4">
                <i className="ri-error-warning-line text-red-500 text-2xl"></i>
              </div>
              <h2 className="text-lg font-bold text-foreground-950 mb-1">
                Error cargando videos
              </h2>
              <p className="text-sm text-foreground-500 mb-5 leading-relaxed">
                No se pudieron cargar los videos. Verificá tu conexión e intentá de nuevo.
              </p>
              <button
                onClick={handleRetry}
                className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-xl bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 cursor-pointer transition-colors whitespace-nowrap"
              >
                <i className="ri-refresh-line text-base"></i>
                Reintentar
              </button>
            </div>
          </main>
        </div>
      </div>
    );
  }

  /* ─── Ready ─── */
  return (
    <div className="min-h-screen bg-background-100">
      {/* Mobile topbar */}
      <header className="lg:hidden sticky top-0 z-30 bg-background-50/98 backdrop-blur-2xl border-b border-background-200 safe-top">
        <div className="flex items-center justify-between px-4 h-14">
          <div className="flex items-center gap-2.5">
            <div className="w-7 h-7 rounded-lg bg-accent-500 flex items-center justify-center">
              <i className="ri-video-line text-background-50 text-xs"></i>
            </div>
            <div>
              <h1 className="text-foreground-950 font-bold text-sm">Videos</h1>
              <p className="text-foreground-400 text-[9px]">{stats.total} videos &bull; {stats.published} publicados</p>
            </div>
          </div>
          <div className="flex items-center gap-1.5">
            <Link
              to="/admin/photos"
              className="px-3 py-1.5 rounded-lg text-[11px] font-semibold text-foreground-500 bg-background-100 hover:bg-background-200 cursor-pointer transition-colors whitespace-nowrap"
            >
              Fotos
            </Link>
            <Link
              to="/"
              className="flex items-center gap-1 text-foreground-500 text-xs hover:text-foreground-700 transition-colors"
            >
              <i className="ri-arrow-left-line text-xs"></i>
            </Link>
          </div>
        </div>
      </header>


          {/* Desktop toolbar */}
          <div className="hidden lg:flex items-center justify-between h-16 px-8 border-b border-background-200 bg-background-50 sticky top-0 z-20">
            <div className="flex items-center gap-4">
              <div>
                <h2 className="text-foreground-950 font-bold text-base">Videos</h2>
                <p className="text-foreground-400 text-xs">
                  {stats.total} videos &bull; {stats.published} publicados
                </p>
              </div>
              <div className="flex items-center gap-1.5">
                <Link
                  to="/admin/photos"
                  className="px-3 py-1.5 rounded-lg text-[11px] font-semibold text-foreground-500 bg-background-100 hover:bg-background-200 cursor-pointer transition-colors whitespace-nowrap"
                >
                  <i className="ri-image-line text-xs mr-1"></i>
                  Fotos
                </Link>
                <Link
                  to="/admin/posts"
                  className="px-3 py-1.5 rounded-lg text-[11px] font-semibold text-foreground-400 bg-background-100 hover:bg-background-200 cursor-pointer transition-colors whitespace-nowrap"
                >
                  <i className="ri-article-line text-xs mr-1"></i>
                  Publicaciones
                </Link>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={handleCreate}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-background-50 text-xs font-semibold cursor-pointer transition-colors whitespace-nowrap ${
                  isPremium ? 'bg-accent-500 hover:bg-accent-600' : 'bg-foreground-300 hover:bg-foreground-400'
                }`}
              >
                {isPremium ? (
                  <i className="ri-add-line text-sm"></i>
                ) : (
                  <i className="ri-lock-line text-sm"></i>
                )}
                Subir video
              </button>
              <Link
                to="/"
                className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-background-100 text-foreground-600 text-xs font-medium hover:bg-background-200 transition-colors cursor-pointer"
              >
                <i className="ri-external-link-line text-xs"></i>
                Ver sitio
              </Link>
            </div>
          </div>

          {/* Content */}
          <div className="p-4 md:p-6 lg:p-8 pb-8 space-y-5">
            {/* Stats mini cards */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="bg-background-50 rounded-2xl border border-background-200 p-4">
                <p className="text-[10px] font-semibold text-foreground-400 uppercase tracking-wide">Videos</p>
                <p className="text-2xl font-bold text-foreground-950 mt-1">{stats.total}</p>
                <p className="text-[10px] text-foreground-400 mt-0.5">{stats.thisMonth} este mes</p>
              </div>
              <div className="bg-background-50 rounded-2xl border border-background-200 p-4">
                <p className="text-[10px] font-semibold text-foreground-400 uppercase tracking-wide">Publicados</p>
                <p className="text-2xl font-bold text-foreground-950 mt-1">{stats.published}</p>
                <div className="flex items-center gap-1.5 mt-0.5">
                  <span className="flex items-center gap-1 text-[10px] text-foreground-400">
                    <span className="w-1.5 h-1.5 rounded-full bg-amber-400"></span>
                    {stats.scheduled} prog.
                  </span>
                  <span className="flex items-center gap-1 text-[10px] text-foreground-400">
                    <span className="w-1.5 h-1.5 rounded-full bg-foreground-300"></span>
                    {stats.drafts} borr.
                  </span>
                </div>
              </div>
              <div className="bg-background-50 rounded-2xl border border-background-200 p-4">
                <p className="text-[10px] font-semibold text-foreground-400 uppercase tracking-wide">Destacados</p>
                <p className="text-2xl font-bold text-foreground-950 mt-1">{stats.featured}</p>
                <div className="flex items-center gap-0.5 mt-0.5">
                  <i className="ri-star-fill text-amber-500 text-[10px]"></i>
                  <span className="text-[10px] text-foreground-400">Videos estrella</span>
                </div>
              </div>
              <StorageBar
                usedGB={storageDisplay.usedGB}
                totalGB={storageDisplay.totalGB}
                percentage={storageDisplay.percentage}
                used={storageDisplay.used}
                total={storageDisplay.total}
                videoCount={storageDisplay.videoCount}
                planLabel={planId === 'free' ? 'Free' : planId === 'pro' ? 'Pro' : 'Premium'}
              />
            </div>

            {/* Upload call-to-action area */}
            <div className={`bg-background-50 rounded-2xl border overflow-hidden ${
              isPremium ? 'border-background-200' : 'border-amber-300/60 bg-amber-50/30'
            }`}>
              <div className="p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                <div>
                  <p className="text-sm font-bold text-foreground-900">
                    {isPremium ? 'Agregar nuevo video' : 'Videos — Exclusivo Premium'}
                  </p>
                  <p className="text-[11px] text-foreground-500 mt-0.5">
                    {isPremium
                      ? 'Subí un archivo o vinculá desde YouTube / Facebook'
                      : 'Actualizá a Premium para subir y gestionar videos ilimitados'}
                  </p>
                </div>
                <button
                  onClick={handleCreate}
                  className={`flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-sm font-semibold cursor-pointer transition-colors whitespace-nowrap ${
                    isPremium
                      ? 'bg-accent-500 text-background-50 hover:bg-accent-600'
                      : 'bg-amber-100 text-amber-700 hover:bg-amber-200'
                  }`}
                >
                  <i className={isPremium ? 'ri-add-line' : 'ri-lock-line'}></i>
                  {isPremium ? 'Nuevo video' : 'Premium requerido'}
                </button>
              </div>

              {/* Plan upgrade banner for non-premium */}
              {!isPremium && (
                <div className="border-t border-amber-200/60 px-4 py-3 flex flex-col sm:flex-row items-start sm:items-center gap-3 bg-amber-50/80">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg bg-accent-100 flex items-center justify-center shrink-0">
                      <i className="ri-vip-diamond-fill text-accent-600 text-sm"></i>
                    </div>
                    <p className="text-xs text-foreground-600 leading-relaxed">
                      <strong className="text-accent-700">Plan Premium</strong> desbloquea videos ilimitados, lives, analytics avanzado y 500 GB de storage.
                    </p>
                  </div>
                  <button
                    onClick={openUpgradeModal}
                    className="shrink-0 px-4 py-2 rounded-xl bg-accent-500 text-background-50 text-xs font-semibold hover:bg-accent-600 cursor-pointer transition-colors whitespace-nowrap ml-auto"
                  >
                    Actualizar a Premium
                  </button>
                </div>
              )}
            </div>

            {/* Video Grid */}
            <div>
              <VideoGrid
                videos={filteredVideos}
                categories={allCategories}
                filterCategory={filterCategory}
                onChangeCategory={setFilterCategory}
                filterStatus={filterStatus}
                onChangeStatus={setFilterStatus}
                sourceFilter={sourceFilter}
                onChangeSource={setSourceFilter}
                searchQuery={searchQuery}
                onSearchChange={setSearchQuery}
                onDelete={handleDelete}
                onToggleFeatured={handleToggleFeatured}
                onEdit={handleEdit}
                onShare={handleShareVideo}
                onHistory={handleHistory}
                isPremium={isPremium}
              />
            </div>
          </div>




      {/* Video Form Modal */}
      <VideoFormModal
        open={formModalOpen}
        video={editingVideo}
        categories={allCategories}
        onClose={() => {
          setFormModalOpen(false);
          setEditingVideo(null);
        }}
        onSave={handleSave}
      />

      {/* Video History Modal */}
      <VideoHistoryModal
        open={historyModalOpen}
        video={historyVideo}
        onClose={() => {
          setHistoryModalOpen(false);
          setHistoryVideo(null);
        }}
      />
    </div>
  );
}

function escapeHtml(str: string): string {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}